import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { CreditCard, LoaderCircle } from "lucide-react";
import type { GetStoreDebtPaymentBalance200 as Balance } from "@workspace/api-client-react";
const money = (p: number) => `£${(p / 100).toFixed(2)}`;
function headers() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("ayrshire_token") ?? ""}`,
  };
}
export default function StoreBalancePayment({
  storeId,
  onUpdated,
  payNow = false,
  refreshKey = 0,
  fallbackBalancePence,
  collapsible = false,
  onViewLedger,
}: {
  storeId: number;
  onUpdated: () => void;
  payNow?: boolean;
  refreshKey?: number;
  fallbackBalancePence?: number;
  collapsible?: boolean;
  onViewLedger?: () => void;
}) {
  const [balance, setBalance] = useState<Balance | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [message, setMessage] = useState("");
  const [expanded, setExpanded] = useState(false);
  const showDetails = !collapsible || expanded;
  const loadVersion = useRef(0);
  const endpoint = `/api/order/stores/${storeId}/debt-payment`;
  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    const r = await fetch(endpoint, { headers: headers() });
    if (!r.ok)
      throw Error(
        "Unable to load card settlement. Sign in as the business owner or an authorised manager and try again.",
      );
    const d = await r.json();
    if (version === loadVersion.current) setBalance(d as Balance);
  }, [endpoint]);
  useEffect(() => {
    let active = true;
    setBalance(null);
    setError("");
    void load().catch((e) => {
      if (active) setError(e.message);
    });
    return () => {
      active = false;
      loadVersion.current++;
    };
  }, [load, refreshKey]);
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("debtPayment");
    if (!id) return;
    void (async () => {
      try {
        const r = await fetch(`${endpoint}/${encodeURIComponent(id)}/verify`, {
          method: "POST",
          headers: headers(),
        });
        const d = await r.json();
        if (!r.ok) throw Error(d.error);
        setMessage(
          d.state === "paid"
            ? `Payment confirmed${d.refundedPence ? " (a refund has been applied)" : ""}. Your account has been updated.`
            : "Payment is not complete. You can resume it below; an unpaid link expires after one hour.",
        );
        await load();
        onUpdated();
      } catch (e) {
        setError(
          e instanceof Error ? e.message : "Payment confirmation unavailable",
        );
      }
    })();
  }, [endpoint]);
  useEffect(() => {
    if (!balance?.pendingPayment) return;
    const timer = setInterval(() => {
      void (async () => {
        try {
          const r = await fetch(
            `${endpoint}/${balance.pendingPayment!.id}/verify`,
            { method: "POST", headers: headers() },
          );
          if (r.ok) {
            const d = await r.json();
            if (d.state === "paid") {
              setMessage("Payment confirmed. Your account has been updated.");
              onUpdated();
            }
            if (d.state !== "pending") await load();
          }
        } catch {}
      })();
    }, 10000);
    return () => clearInterval(timer);
  }, [balance?.pendingPayment?.id, endpoint, load]);
  const pay = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const r = await fetch(endpoint, {
        method: "POST",
        headers: headers(),
        body: "{}",
      });
      const d = await r.json();
      if (!r.ok) throw Error(d.error || "Could not open payment");
      if (d.state === "paid") {
        setMessage("Payment confirmed. Your account has been updated.");
        await load();
        onUpdated();
        return;
      }
      if (!d.url) throw Error('This payment is still being confirmed. Please refresh your balance again shortly.');
      const url = new URL(d.url);
      if (url.protocol !== "https:" || url.hostname !== "checkout.stripe.com")
        throw Error("Invalid secure payment link");
      window.location.assign(url.href);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not open payment");
      await load().catch(() => {});
    } finally {
      setBusy(false);
    }
  };
  return (
    <section
      className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 mb-4 space-y-3"
      aria-label="Settle your account"
    >
      {collapsible && <button type="button" aria-label="Show balance details" aria-expanded={expanded} onClick={()=>setExpanded(v=>!v)} className="flex w-full min-h-11 items-center justify-between gap-3 text-left font-semibold text-slate-900"><span>Your Ayrshire Hub balance</span><span aria-hidden="true">{expanded?'⌃':'⌄'}</span></button>}
      {showDetails && <div>
        {!collapsible && <h2 className="font-semibold text-slate-900">
          Your Ayrshire Hub balance
        </h2>}
        <p className="text-sm text-slate-600">
          Review your charges below and settle existing delivery and service
          charges securely by card.
        </p>
      </div>}
      {!balance && fallbackBalancePence !== undefined && <p className="text-2xl font-bold text-slate-900">{money(fallbackBalancePence)} <span className="text-sm font-normal text-slate-500">ledger balance</span></p>}
      {balance ? (
        <>
          <p className="text-2xl font-bold text-slate-900">
            {money(
              balance.accountCreditPence > 0
                ? balance.accountCreditPence
                : balance.outstandingPence,
            )}{" "}
            <span className="text-sm font-normal text-slate-500">
              {balance.accountCreditPence > 0
                ? "account credit"
                : "outstanding"}
            </span>
          </p>
          {showDetails && balance.accountCreditPence > 0 && (
            <p className="text-sm text-slate-600">
              Your credit will reduce future delivery and service charges.
            </p>
          )}
          {showDetails && balance.reservedPence > 0 && (
            <p className="text-sm text-slate-600">
              {money(balance.reservedPence)} is reserved for payments already in
              progress. It will only reduce your balance once payment is
              confirmed.
            </p>
          )}
          {balance.pendingPayment || balance.payablePence >= 30 ? (
            <Button
              className="w-full sm:w-auto gap-2"
              disabled={busy}
              onClick={() => void pay()}
            >
              {busy ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="h-4 w-4" />
              )}
              {busy
                ? "Opening secure payment…"
                : balance.pendingPayment
                  ? `Resume payment of ${money(balance.pendingPayment.amountPence)}`
                  : payNow ? `Pay now · ${money(balance.payablePence)} by card` : `Pay ${money(balance.payablePence)} by card`}
            </Button>
          ) : (
            <p className="text-sm text-slate-600">
              {balance.outstandingPence === 0
                ? "Your account is up to date."
                : balance.payablePence > 0
                  ? "The remaining balance is below the 30p card minimum."
                  : "Your balance is covered by payments in progress."}
            </p>
          )}
        </>
      ) : (
        !error && (
          <p className="text-sm text-slate-500">Checking your balance…</p>
        )
      )}
      {collapsible && <a href={`/business/finance/${storeId}`} onClick={onViewLedger ? e=>{e.preventDefault();onViewLedger()} : undefined} className="inline-flex min-h-11 items-center rounded-lg border border-slate-300 px-4 text-sm font-semibold text-slate-900">View ledger</a>}
      {message && (
        <p role="status" className="text-sm text-emerald-700">
          {message}
        </p>
      )}
      {error && (
        <div role="alert" className="text-sm text-rose-700">
          {error}{" "}
          {localStorage.getItem("ayrshire_token") ? (
            <button
              className="underline"
              onClick={() =>
                void load()
                  .then(() => setError(""))
                  .catch(() => {})
              }
            >
              Refresh balance
            </button>
          ) : (
            <a
              className="underline"
              href={`/login?returnTo=${encodeURIComponent(`/business/finance/${storeId}`)}`}
            >
              Sign in as an owner or manager
            </a>
          )}
        </div>
      )}
      {showDetails && <p className="text-xs text-slate-500">
        This pays down your account balance; it does not create a new order.
      </p>}
    </section>
  );
}
